import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Button from '@material-ui/core/Button';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import AddIcon from '@material-ui/icons/Add';
import type { CalendarArgs } from '@this/domain/appoint/calendar';
import Calendar from '@this/domain/appoint/calendar';
import Utils from '@this/domain/appoint/utils';
import { useHistory } from 'react-router-dom';
import { H2, Section, ButtonProgress } from '../common';

interface Response {
  calendars: CalendarArgs[];
}

const Calendars = observer(() => {
  const history = useHistory();
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [isCalendarsLoading, setIsCalendarsLoading] = useState<boolean>(false);
  const [isDefaultChanging, setIsDefaultChanging] = useState<boolean>(false);

  const fetchCalendars = useCallback(async () => {
    setIsCalendarsLoading(true);
    const res = await axios.get<Response>('/aicalendar/calendars.json');
    const data = res.data;
    const cals = data.calendars.map(c => new Calendar(c));
    setCalendars(cals);
    setIsCalendarsLoading(false);
    setIsDefaultChanging(false);
  }, [calendars]);

  // マウント時に1回発動
  useEffect(() => {
    fetchCalendars();
    Utils.trackEvent('[cal][view] calendars');
  }, []);

  const handleDefaultCalendarChange = async (id: string) => {
    setIsDefaultChanging(true);
    await axios.put(`/aicalendar/auth/${id}`, {});
    fetchCalendars();
  };

  const handleCalendarDisconnect = async (id: string) => {
    await axios.delete(`/aicalendar/auth/${id}`, {});
    location.reload();
  };

  return (
    <TheContainer maxWidth="md">
      <Section>
        <Box display="flex">
          <Box flexGrow={999}>
            <TheH2>カレンダー連携</TheH2>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              style={{ textTransform: 'none' }}
              onClick={() => {
                location.href = history.createHref({ pathname: '/auth/new?provider=outlook' });
              }}
            >
              <AddIcon fontSize="small" />
              Outlook Calendarと連携
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ marginLeft: '10px', textTransform: 'none' }}
              onClick={() => {
                location.href = history.createHref({ pathname: '/auth/new?provider=google' });
              }}
            >
              <AddIcon fontSize="small" />
              Google Calendarと連携
            </Button>
          </Box>
        </Box>
      </Section>
      <TheCard>
        {isCalendarsLoading && <ButtonProgress />}
        <RadioGroup>
          <TheTable>
            <tbody>
              <tr>
                <TheTh>デフォルト</TheTh>
                <TheTh>カレンダー名</TheTh>
                <TheTh>メールアドレス</TheTh>
                <TheTh />
              </tr>
              {calendars.map(c => (
                <tr key={c.id}>
                  <th>
                    <Radio
                      checked={c.isDefault}
                      disabled={isDefaultChanging}
                      onChange={e => handleDefaultCalendarChange(e.target.value)}
                      value={c.authDataId}
                    />
                  </th>
                  <TheTd>{c.name}</TheTd>
                  <TheTd>{c.email}</TheTd>
                  <TheTd>
                    <Button color="primary" onClick={() => handleCalendarDisconnect(c.authDataId)}>
                      連携解除
                    </Button>
                  </TheTd>
                </tr>
              ))}
            </tbody>
          </TheTable>
        </RadioGroup>
      </TheCard>
    </TheContainer>
  );
});

const TheContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 54px - 64px - 40px);
`;

const TheH2 = styled(H2)`
  margin-bottom: 0;
`;

const TheCard = styled(Card)`
  position: relative;
  flex-grow: 999;
  padding: 12px 0 0;
`;

const TheTable = styled.table`
  width: 100%;
  border-spacing: 0;
`;

const TheTh = styled.th`
  padding: 10px;
  color: ${props => props.theme.grayTextColor};
`;

const TheTd = styled.td`
  padding: 10px;
  border-top: solid 1px ${props => props.theme.grayBorderColor};
`;

export default Calendars;
